import React, { ElementType, ReactNode } from 'react';
import { TabNav as RadixTabNav } from '@radix-ui/themes';

export type TabNavRootProps = React.ComponentProps<typeof RadixTabNav.Root>;

interface BaseTabNavLinkProps {
  children?: ReactNode;
  as?: ElementType;
  component?: ElementType;
}

export type TabNavLinkProps<T extends ElementType = 'button'> =
  BaseTabNavLinkProps &
    Omit<React.ComponentProps<T>, keyof BaseTabNavLinkProps> &
    Omit<React.ComponentProps<typeof RadixTabNav.Link>, 'asChild'>;

/**
 * TabNav — simple wrapper for Radix Themes TabNav. In the future, this component will be extended and adapted for the project design system.
 *
 * Usage example:
 * ```tsx
 * import { TabNav } from '@/components/ui/TabNav';
 *
 * <TabNav.Root>
 *   <TabNav.Link href="#tab1">Tab 1</TabNav.Link>
 *   <TabNav.Link href="#tab2">Tab 2</TabNav.Link>
 *   <TabNav.Link href="#tab3">Tab 3</TabNav.Link>
 * </TabNav.Root>
 * ```
 */

export const TabNavRoot: React.FC<TabNavRootProps> = props => {
  return <RadixTabNav.Root {...props} />;
};

/**
 * TabNavLink — polymorphic wrapper for Radix Themes TabNav.Link.
 */
export const TabNavLink = React.forwardRef(
  <T extends ElementType = 'button'>(
    { as: Component, component, children, ...props }: TabNavLinkProps<T>,
    ref: React.ForwardedRef<unknown>
  ) => {
    const ActualComponent = Component || component || 'button';

    if (ActualComponent !== 'button') {
      return (
        <RadixTabNav.Link asChild {...props}>
          <ActualComponent>{children}</ActualComponent>
        </RadixTabNav.Link>
      );
    }

    return (
      <RadixTabNav.Link ref={ref} {...props}>
        {children}
      </RadixTabNav.Link>
    );
  }
);

TabNavLink.displayName = 'TabNavLink';
